import React from "react";
import { TeamRoster } from "./players";

interface PlayerGridProps {
  rosters: TeamRoster[];
  selectedPlayerId: number | null;
  /** Receives the team the player was picked under, which sets the event's team. */
  onSelect: (playerId: number, teamId: number) => void;
  excludedPlayerIds?: number[];
  /** Renders a leading opt-out button, for steps that can be left empty. */
  noneLabel?: string;
  noneSelected?: boolean;
  onSelectNone?: () => void;
  /** Off for a single ungrouped list, such as league-wide search results. */
  showTeamNames?: boolean;
  emptyMessage?: string;
}

/**
 * Players as wrapping buttons — jersey number large, name underneath —
 * grouped by team, so one tap records both the player and their team.
 */
function PlayerGrid({
  rosters,
  selectedPlayerId,
  onSelect,
  excludedPlayerIds = [],
  noneLabel,
  noneSelected = false,
  onSelectNone,
  showTeamNames = true,
  emptyMessage = "No players available.",
}: PlayerGridProps) {
  const excluded = new Set(excludedPlayerIds);

  return (
    <div>
      {noneLabel && onSelectNone && (
        <div className="ee-option-grid ee-option-grid--lead">
          <button
            type="button"
            className={`ee-option-btn${
              noneSelected ? " ee-option-btn--selected" : ""
            }`}
            aria-pressed={noneSelected}
            onClick={onSelectNone}
          >
            <span className="ee-option-label">{noneLabel}</span>
          </button>
        </div>
      )}

      {rosters.map((roster) => {
        const players = roster.players.filter(
          (player) => !excluded.has(player.id),
        );

        return (
          <section key={roster.teamId} className="ee-team-group">
            {showTeamNames && (
              <h6 className="ee-team-name">{roster.teamName}</h6>
            )}
            {players.length === 0 ? (
              <p className="ee-empty">{emptyMessage}</p>
            ) : (
              <div className="ee-player-grid">
                {players.map((player) => {
                  const isSelected = selectedPlayerId === player.id;

                  return (
                    <button
                      key={player.id}
                      type="button"
                      className={`ee-player-btn${
                        isSelected ? " ee-player-btn--selected" : ""
                      }`}
                      aria-pressed={isSelected}
                      onClick={() => onSelect(player.id, roster.teamId)}
                    >
                      <span className="ee-player-jersey">
                        {player.jerseyLabel}
                      </span>
                      <span className="ee-player-name">{player.name}</span>
                      {player.isSpare && (
                        <span className="ee-player-tag">Spare</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

export default PlayerGrid;
